import { Collapsible, CollapsibleContent } from "@workspace/frontend-ui";
import { cva } from "class-variance-authority";
import type { CSSProperties, FC } from "react";
import useRewardCenter from "../../actors/useRewardCenter";
import Arcs from "../../assets/Arcs";
import useTranslations from "../../i18n/useTranslations";
import { TILE_COLOR } from "../../palette";
import RewardAction from "./RewardAction";
import RewardInfoButton from "./RewardInfoButton";
import RewardNote from "./RewardNote";
import RewardOptions from "./RewardOptions";
import RewardTile from "./RewardTile";
import { REWARD_LABEL, REWARD_TILE } from "./rewardMeta";

type Props = {
  id: string;
};

const rewardCardVariants = cva(
  "relative isolate overflow-hidden rounded-2xl border-t border-t-foreground/5 bg-card shadow-md before:absolute before:inset-y-0 before:left-0 before:w-0.75 before:bg-(--rail)",
  {
    variants: {
      state: {
        ready: "",
        claiming: "",
        confirmed: "bg-primary/15 before:bg-primary",
        cooldown: "",
        expiring: "",
        zero: "",
        locked: "before:bg-foreground/15",
        error: "",
        open: "",
        claimed: "bg-primary/15 before:bg-primary",
      },
    },
  },
);

const RewardCard: FC<Props> = ({ id }) => {
  const {
    state: { rewards, expandedRewardId },
    send,
  } = useRewardCenter();
  const t = useTranslations();
  const reward = rewards?.find((item) => item.id === id);
  if (!reward) return null;

  const style = {
    "--rail": TILE_COLOR[REWARD_TILE[reward.type]],
  } as CSSProperties;

  return (
    <Collapsible
      open={expandedRewardId === id}
      onOpenChange={() => send("rewardCenter", { type: "toggleReward", id })}
      data-slot="reward-card"
      data-reward-state={reward.state}
      className={rewardCardVariants({ state: reward.state })}
      style={style}
    >
      <Arcs variant="card" />
      <div className="flex items-center gap-3 p-3">
        <RewardTile reward={reward} />
        <p className="min-w-0 flex-1 truncate font-semibold text-foreground text-sm">
          {t(REWARD_LABEL[reward.type])}
        </p>
        <RewardAction reward={reward} />
        {reward.info && <RewardInfoButton />}
      </div>
      <CollapsibleContent className="rc-collapse">
        <p className="px-3 pb-3 text-muted-foreground text-xs">{reward.info}</p>
      </CollapsibleContent>
      {reward.state === "open" && <RewardOptions reward={reward} />}
      {reward.note && <RewardNote note={reward.note} />}
    </Collapsible>
  );
};

export default RewardCard;
